import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { movies } from "@/mocks/movies";
import MovieCard from "@/components/feature/MovieCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SectionHeading from "@/components/feature/SectionHeading";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import ShareBar from "./components/ShareBar";

export default function DetailPage() {
  const { id } = useParams();
  const movie = movies.find((m) => m.id === id);

  useDocumentMeta(
    movie ? `${movie.title} - 欧美三级影视详情` : "影片详情 - 欧美三级影视详情",
    movie
      ? `${movie.title}（${movie.titleEn}，${movie.year}）剧情介绍与影片基本信息，类型：${movie.genres.join("、")}，导演：${movie.director}，主演：${movie.cast}，片长：${movie.duration}。`
      : "欧美三级影视详情页，展示影片剧情介绍与基本信息。",
    movie ? `${movie.title},${movie.titleEn},${movie.genres.join(",")},${movie.region}电影,影视详情` : "影视详情"
  );

  const related = useMemo(() => {
    if (!movie) return [];
    return movies
      .filter(
        (m) =>
          m.id !== movie.id &&
          (m.director === movie.director || m.genres.some((g) => movie.genres.includes(g)))
      )
      .slice(0, 4);
  }, [movie]);

  const infoRows = movie
    ? [
        { label: "类型", value: movie.genres.join(" / ") },
        { label: "导演", value: movie.director },
        { label: "主演", value: movie.cast },
        { label: "上映时间", value: `${movie.year} 年` },
        { label: "片长", value: movie.duration },
        { label: "制片地区", value: movie.region },
      ]
    : [];

  if (!movie) {
    return (
      <div className="mx-auto flex max-w-[1240px] flex-col items-center justify-center px-4 py-24 text-center md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-3xl text-secondary-500">
          <i className="ri-movie-2-line"></i>
        </span>
        <h1 className="mt-4 font-heading text-xl font-bold text-foreground-950">未找到该影片</h1>
        <p className="mt-2 text-sm text-secondary-500">该影片可能已下架或链接有误，请从分类页重新选择。</p>
        <div className="mt-6 flex gap-3">
          <Link
            to="/category"
            className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700"
          >
            <i className="ri-grid-line"></i>
            返回电影分类
          </Link>
          <Link
            to="/"
            className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
          >
            回到首页
          </Link>
        </div>
      </div>
    );
  }

  return (
    <article>
      {/* Banner */}
      <div className="relative h-56 w-full overflow-hidden bg-primary-950 md:h-80">
        <img
          src={movie.backdrop || movie.cover}
          alt={`${movie.title} 背景图`}
          title={`${movie.title} - 欧美三级影视详情`}
          className="h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/70 to-primary-950/40"></div>
        <div className="absolute inset-x-0 bottom-0">
          <div className="mx-auto max-w-[1240px] px-4 pb-6 md:px-6 md:pb-8">
            <div className="[&_*]:!text-background-100/80">
              <Breadcrumb items={[{ label: "电影分类", to: "/category" }, { label: movie.title }]} />
            </div>
            <h1 className="mt-3 font-heading text-2xl font-bold text-background-50 md:text-4xl">{movie.title}</h1>
            <p className="mt-1 text-sm text-primary-100/80 md:text-base">{movie.titleEn}</p>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1240px] px-4 py-8 md:px-6 md:py-10">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-[280px_1fr]">
          {/* 左侧：封面 + 标签 + 分享 */}
          <div className="space-y-5">
            <div className="overflow-hidden rounded-lg border border-background-200/80 bg-background-50">
              <div className="aspect-[2/3] w-full">
                <img
                  src={movie.cover}
                  alt={`${movie.title} 海报`}
                  title={`${movie.title} - 欧美三级影视详情`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>

            <div className="rounded-lg border border-background-200/80 bg-background-50 p-4">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground-900">
                <i className="ri-price-tag-3-line text-base text-accent-600"></i>
                影片标签
              </h3>
              <div className="mt-3 flex flex-wrap gap-2">
                {movie.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-md bg-accent-100 px-2.5 py-1 text-xs font-medium text-accent-800"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <ShareBar title={`${movie.title} - 欧美三级影视详情`} />
          </div>

          {/* 右侧：基本信息 + 剧情 */}
          <div className="space-y-6">
            <div className="flex flex-wrap items-center gap-3">
              <span className="flex items-center gap-1.5 rounded-md bg-primary-950 px-3 py-1.5 text-sm font-semibold text-accent-300">
                <i className="ri-star-fill"></i>
                {movie.rating.toFixed(1)} 分
              </span>
              <span className="rounded-md bg-secondary-100 px-3 py-1.5 text-sm font-medium text-secondary-800">
                {movie.year} 年
              </span>
              <span className="rounded-md bg-secondary-100 px-3 py-1.5 text-sm font-medium text-secondary-800">
                {movie.region}
              </span>
              <span className="rounded-md bg-secondary-100 px-3 py-1.5 text-sm font-medium text-secondary-800">
                {movie.duration}
              </span>
            </div>

            <div className="rounded-lg border border-background-200/80 bg-background-50 p-5 md:p-6">
              <h2 className="font-heading text-lg font-bold text-foreground-950">影片基本信息</h2>
              <dl className="mt-4 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
                {infoRows.map((row) => (
                  <div key={row.label} className="flex gap-3 border-b border-background-200/70 pb-2.5">
                    <dt className="w-20 shrink-0 text-sm text-secondary-500">{row.label}</dt>
                    <dd className="text-sm font-medium text-foreground-900">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="rounded-lg border border-background-200/80 bg-background-50 p-5 md:p-6">
              <h2 className="font-heading text-lg font-bold text-foreground-950">剧情介绍</h2>
              <div className="mt-4 space-y-3.5 text-sm leading-7 text-foreground-700">
                {movie.plot.split("\n\n").map((para, idx) => (
                  <p key={idx}>{para}</p>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-accent-200 bg-accent-50 p-4 text-xs leading-relaxed text-accent-900">
              <i className="ri-information-line mr-1"></i>
              本平台为内容资讯展示站点，影片资料为演示示例，不提供影片的在线播放、下载或付费服务，请前往正规渠道观看正版内容。
            </div>

            <div className="flex flex-wrap gap-3">
              <Link
                to="/category"
                className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700"
              >
                <i className="ri-grid-line"></i>
                返回分类
              </Link>
              <Link
                to="/"
                className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                <i className="ri-home-4-line"></i>
                返回首页
              </Link>
            </div>
          </div>
        </div>

        {related.length > 0 && (
          <section className="mt-12 md:mt-16">
            <SectionHeading title="相关推荐" subtitle="根据类型与导演为你推荐" />
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 md:gap-4">
              {related.map((m) => (
                <MovieCard key={m.id} movie={m} />
              ))}
            </div>
          </section>
        )}
      </div>
    </article>
  );
}